import * as React from 'react';
import { Alert, Box, Grid, Paper, Skeleton, Typography } from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import NumbersIcon from '@mui/icons-material/Numbers';
import { withTranslation } from 'react-i18next';
import DataTable from '../../shared/DataTable';
import MetricCard from '../components/MetricCard';
import { useInventoryReport } from '../hooks/useReportsApi';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' }).format(value);
};

const formatQuantity = (value) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('fi-FI', { maximumFractionDigits: 2 }).format(value);
};

const formatDate = (value) => {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  return `${Number(day)}.${Number(month)}.${year}`;
};

function InventoryValueSection({ t, year, month }) {
  const { data, loading, error } = useInventoryReport(year, month);

  if (error) {
    return <Alert severity="error">{t('reports.error')}: {error}</Alert>;
  }

  // Stock tracking that only started later is not the same thing as an empty
  // warehouse, and an accountant must not read a bare 0 as the latter.
  const startedLater =
    data?.ledger_starts_on && data.as_of_date && data.as_of_date < data.ledger_starts_on;

  const columns = [
    {
      key: 'part_name',
      label: t('reports.inventory.part'),
      render: (row) => (
        <Box sx={{ maxWidth: { xs: 170, sm: 240, md: 320 }, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.part_name}
          </Typography>
          {/* The code column is hidden on phones, so it rides along here instead. */}
          <Typography
            sx={{ display: { xs: 'block', sm: 'none' }, fontSize: 11, color: 'text.secondary' }}
          >
            {row.code || '-'}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'code',
      label: t('reports.inventory.code'),
      width: '1%',
      noWrap: true,
      hideBelow: 'sm',
      render: (row) => row.code || '-',
    },
    {
      key: 'shelf_location',
      label: t('reports.inventory.shelfLocation'),
      width: '1%',
      noWrap: true,
      hideBelow: 'lg',
      render: (row) => row.shelf_location || '-',
    },
    {
      key: 'quantity',
      label: t('reports.inventory.quantity'),
      align: 'right',
      width: '1%',
      noWrap: true,
      render: (row) => (
        <Typography sx={{ fontSize: 14, color: Number(row.quantity) < 0 ? 'error.main' : 'inherit' }}>
          {formatQuantity(row.quantity)}
        </Typography>
      ),
    },
    {
      key: 'avg_unit_cost',
      label: t('reports.inventory.avgUnitCost'),
      align: 'right',
      width: '1%',
      noWrap: true,
      hideBelow: 'md',
      render: (row) => `${formatCurrency(row.avg_unit_cost)}${row.is_estimate ? ' *' : ''}`,
    },
    {
      key: 'value',
      label: t('reports.inventory.valueColumn'),
      align: 'right',
      width: '1%',
      noWrap: true,
      render: (row) => (
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{formatCurrency(row.value)}</Typography>
      ),
    },
  ];

  return (
    <Box data-testid="reports-inventory-value">
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {t('reports.inventory.title')}
        </Typography>
        {loading ? (
          <Skeleton variant="text" width={260} />
        ) : (
          <Typography variant="body2" color="text.secondary" data-testid="reports-inventory-as-of">
            {t('reports.inventory.asOf', { date: formatDate(data?.as_of_date) })}
          </Typography>
        )}
        {data?.clamped_to_today && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('reports.inventory.partialMonthNotice', { date: formatDate(data.as_of_date) })}
          </Alert>
        )}
        {startedLater && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('reports.inventory.trackingStartedLater', {
              date: formatDate(data.ledger_starts_on),
            })}
          </Alert>
        )}
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <MetricCard
            icon={WarehouseIcon}
            label={t('reports.inventory.totalValue')}
            value={data?.total_value}
            format="currency"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            icon={Inventory2Icon}
            label={t('reports.inventory.itemsTracked')}
            value={data?.items_tracked}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            icon={NumbersIcon}
            label={t('reports.inventory.totalQuantity')}
            value={data?.total_quantity}
            format="decimal"
            loading={loading}
          />
        </Grid>
      </Grid>

      {data?.estimated_rows > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('reports.inventory.estimateNotice', { n: data.estimated_rows })}
        </Alert>
      )}
      {data?.drifted_rows > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('reports.inventory.driftNotice', { n: data.drifted_rows })}
        </Alert>
      )}

      <DataTable
        columns={columns}
        data={data?.rows || []}
        loading={loading}
        alwaysVisible
        horizontalScroll
        tableTestId="reports-inventory-value-table"
        emptyIcon={<Inventory2Icon />}
        emptyTitle={t('reports.inventory.noItems')}
        emptyDescription={t('reports.inventory.noItemsDescription')}
      />

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        {t('reports.inventory.vatNote')}
      </Typography>
    </Box>
  );
}

export default withTranslation()(InventoryValueSection);
